import { Helmet } from 'react-helmet-async';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Button, Card, Chip, Container, Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton, LinearProgress,
  List, ListItemButton, ListItemText, MenuItem, Stack, Switch, FormControlLabel, Table, TableBody, TableCell, TableHead, TableRow,
  TextField, Tooltip, Typography, Checkbox, Box, InputAdornment,
} from '@mui/material';
import axios from 'axios';
import {
  addDoc, collection, doc, getDocs, getFirestore, orderBy, query, serverTimestamp, updateDoc, where,
} from 'firebase/firestore';
import { getDownloadURL, getStorage, ref as storageRef, uploadBytes } from 'firebase/storage';
import Iconify from '../components/iconify';
import firebaseApp from '../utils/firebaseConfig';
import { getCurrentIdentity } from '../protectedRoute';
import { formatPhoneDisplay, phoneDigits } from '../utils/phone';

const db = getFirestore(firebaseApp);
const storage = getStorage(firebaseApp);
const FUNCTIONS_BASE = 'https://us-central1-bb-target-center.cloudfunctions.net';
const STATUS = {
  waiting: { label: 'En espera', color: 'info' },
  notified: { label: 'Avisado', color: 'warning' },
  fulfilled: { label: 'Entregado', color: 'success' },
  removed: { label: 'Quitado', color: 'default' },
};
const toDate = (v) => (v && typeof v.toDate === 'function' ? v.toDate() : (v ? new Date(v) : null));
const fmt = (v) => { const d = toDate(v); return d ? d.toLocaleString('es-PR', { day: '2-digit', month: 'short', hour: 'numeric', minute: '2-digit' }) : '—'; };
const staffName = () => { const i = getCurrentIdentity(); return i.displayName || i.email || 'staff'; };

// Lista de espera de productos (pedido Lola 2026-10-19, estilo Waitwhile): el
// staff crea el producto, ve la cola por orden de llegada, anota gente a mano
// y avisa (email + push) cuando llega. El cliente se anota y ve su posición
// desde la app o /waitlist.
export default function WaitlistPage() {
  const [products, setProducts] = useState([]);
  const [entries, setEntries] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showClosed, setShowClosed] = useState(false);
  const [search, setSearch] = useState(''); // buscador de productos (audio Lola 2026-10-19: la lista va a crecer)
  const [checked, setChecked] = useState({});
  // diálogos
  const [prodDialog, setProdDialog] = useState(null); // { id?, name, description, active, available }
  const [addOpen, setAddOpen] = useState(false);
  const [add, setAdd] = useState({ phone: '', name: '', email: '', note: '' });
  const [addLookup, setAddLookup] = useState('idle');
  const [notifyOpen, setNotifyOpen] = useState(false);
  const [notifyMode, setNotifyMode] = useState('next'); // next | all | selected
  const [notifyCount, setNotifyCount] = useState(1);
  const [notifyMsg, setNotifyMsg] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [photo, setPhoto] = useState(null); // foto del producto a subir (Lola 2026-10-19)

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const [ps, es] = await Promise.all([
        getDocs(query(collection(db, 'waitlist_products'), orderBy('name'))),
        getDocs(collection(db, 'waitlist_entries')),
      ]);
      const plist = ps.docs.map((d) => ({ id: d.id, ...d.data() }));
      setProducts(plist);
      setEntries(es.docs.map((d) => ({ id: d.id, ...d.data() })));
      setSelectedId((cur) => cur || (plist[0] ? plist[0].id : ''));
    } catch (e) { setError(e.message); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const selected = products.find((p) => p.id === selectedId) || null;
  const norm = (v) => String(v || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
  const visibleProducts = products.filter((p) => !search.trim() || norm(`${p.name} ${p.description || ''}`).includes(norm(search)));
  const countOpen = (pid) => entries.filter((e) => e.productId === pid && ['waiting', 'notified'].includes(e.status)).length;
  const queue = useMemo(() => {
    const list = entries.filter((e) => e.productId === selectedId);
    const open = list.filter((e) => ['waiting', 'notified'].includes(e.status)).sort((a, b) => (toDate(a.joinedAt) || 0) - (toDate(b.joinedAt) || 0));
    const closed = list.filter((e) => !['waiting', 'notified'].includes(e.status)).sort((a, b) => (toDate(b.joinedAt) || 0) - (toDate(a.joinedAt) || 0));
    return { open: open.map((e, i) => ({ ...e, position: i + 1 })), closed };
  }, [entries, selectedId]);
  const selectedIds = Object.keys(checked).filter((k) => checked[k]);

  // ---- productos ----
  const saveProduct = async () => {
    const p = prodDialog;
    if (!p.name.trim()) return;
    setBusy(true);
    try {
      const data = { name: p.name.trim(), description: (p.description || '').trim(), active: p.active !== false, available: Boolean(p.available), imageUrl: p.imageUrl || '', updatedAt: serverTimestamp(), updatedBy: staffName() };
      if (photo) {
        const safe = photo.name.replace(/[^a-zA-Z0-9._-]/g, '_');
        const r = storageRef(storage, `waitlist-products/${Date.now()}-${safe}`);
        await uploadBytes(r, photo, { contentType: photo.type || 'image/jpeg' });
        data.imageUrl = await getDownloadURL(r);
      }
      if (p.id) await updateDoc(doc(db, 'waitlist_products', p.id), data);
      else { const ref = await addDoc(collection(db, 'waitlist_products'), { ...data, createdAt: serverTimestamp(), createdBy: staffName() }); setSelectedId(ref.id); }
      setProdDialog(null); setPhoto(null); await load();
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  // ---- anotar a mano ----
  const lookupPhone = async () => {
    const d = phoneDigits(add.phone);
    if (d.length < 7) return;
    setAddLookup('searching');
    try {
      let snap = await getDocs(query(collection(db, 'members'), where('phone', '==', d)));
      if (snap.empty && d.length === 10) snap = await getDocs(query(collection(db, 'members'), where('phone', '==', `${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6)}`)));
      if (snap.empty) { setAddLookup('notfound'); return; }
      const m = snap.docs[0].data();
      setAdd((a) => ({ ...a, name: a.name || m.fullName || '', email: a.email || m.email || '' }));
      setAddLookup('found');
    } catch (e) { setAddLookup('idle'); }
  };
  const submitAdd = async () => {
    if (!selected) return;
    setBusy(true); setError('');
    try {
      const r = await axios.post(`${FUNCTIONS_BASE}/joinWaitlist`, { productId: selected.id, phone: phoneDigits(add.phone), name: add.name.trim(), email: add.email.trim(), note: add.note, source: 'staff', staffName: staffName() });
      setAddOpen(false); setAdd({ phone: '', name: '', email: '', note: '' }); setAddLookup('idle');
      if (r.data.alreadyIn) setResult({ severity: 'info', text: `${add.name || 'El cliente'} ya estaba en esta lista (posición ${r.data.entry.position}).` });
      else setResult({ severity: 'success', text: `Anotado en la posición ${r.data.entry.position}.${r.data.accountCreated ? ` Se le creó cuenta con PIN ${r.data.pin}.` : ''}` });
      await load();
    } catch (e) { setError((e.response && e.response.data && e.response.data.error) || e.message); } finally { setBusy(false); }
  };

  // ---- avisar ----
  const submitNotify = async () => {
    if (!selected) return;
    setBusy(true); setError('');
    try {
      const body = { productId: selected.id, message: notifyMsg, staffName: staffName() };
      if (notifyMode === 'selected') body.entryIds = selectedIds;
      else if (notifyMode === 'next') body.count = Number(notifyCount) || 1;
      const r = await axios.post(`${FUNCTIONS_BASE}/adminNotifyWaitlist`, body);
      const emails = r.data.results.filter((x) => x.email).length; const pushes = r.data.results.reduce((s, x) => s + (x.push || 0), 0);
      setResult({ severity: 'success', text: `Avisado a ${r.data.notified} persona(s): ${emails} email(s), ${pushes} push.` });
      setNotifyOpen(false); setChecked({}); await load();
    } catch (e) { setError((e.response && e.response.data && e.response.data.error) || e.message); } finally { setBusy(false); }
  };

  const setStatus = async (entry, status) => {
    try {
      await updateDoc(doc(db, 'waitlist_entries', entry.id), { status, [`${status}At`]: serverTimestamp(), [`${status}By`]: staffName() });
      await load();
    } catch (e) { setError(e.message); }
  };

  return (
    <>
      <Helmet><title> Lista de espera | BBTC </title></Helmet>
      <Container maxWidth="xl">
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
          <div>
            <Typography variant="h4">Lista de espera</Typography>
            <Typography variant="body2" color="text.secondary">Productos que no hay: los clientes se anotan (app o bbtc-dashboard.web.app/waitlist) y se les avisa cuando llegan.</Typography>
          </div>
          <Button variant="contained" startIcon={<Iconify icon="eva:plus-fill" />} onClick={() => setProdDialog({ name: '', description: '', active: true, available: false })}>Nuevo producto</Button>
        </Stack>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
        {result && <Alert severity={result.severity} sx={{ mb: 2 }} onClose={() => setResult(null)}>{result.text}</Alert>}
        {loading && <LinearProgress sx={{ mb: 2 }} />}
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Card>
              <Box sx={{ p: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                <TextField fullWidth size="small" placeholder="Buscar producto…" value={search} onChange={(e) => setSearch(e.target.value)} InputProps={{ startAdornment: <InputAdornment position="start"><Iconify icon="eva:search-fill" /></InputAdornment> }} />
              </Box>
              <List dense disablePadding sx={{ maxHeight: '70vh', overflow: 'auto' }}>
                {products.length === 0 && !loading && <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>Crea el primer producto con "Nuevo producto".</Typography>}
                {products.length > 0 && visibleProducts.length === 0 && <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>Ningún producto coincide con "{search}".</Typography>}
                {visibleProducts.map((p) => (
                  <ListItemButton key={p.id} selected={p.id === selectedId} onClick={() => { setSelectedId(p.id); setChecked({}); }} sx={{ opacity: p.active === false ? 0.5 : 1 }}>
                    {p.imageUrl ? <Box component="img" src={p.imageUrl} alt="" sx={{ width: 44, height: 44, objectFit: 'cover', borderRadius: 1, mr: 1.5, flexShrink: 0 }} /> : <Box sx={{ width: 44, height: 44, borderRadius: 1, mr: 1.5, bgcolor: 'action.hover', flexShrink: 0 }} />}
                    <ListItemText primary={p.name} secondary={p.description || null} primaryTypographyProps={{ fontWeight: 600 }} />
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      {p.available && <Chip size="small" color="success" label="Disponible" />}
                      {p.active === false && <Chip size="small" label="Cerrado" />}
                      <Chip size="small" variant="outlined" label={countOpen(p.id)} title="Personas en lista" />
                    </Stack>
                  </ListItemButton>
                ))}
              </List>
            </Card>
          </Grid>
          <Grid item xs={12} md={8}>
            {selected ? (
              <Card sx={{ p: 2 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" useFlexGap gap={1} mb={2}>
                  <div>
                    <Typography variant="h6">{selected.name}</Typography>
                    <Typography variant="caption" color="text.secondary">{queue.open.length} en lista · {selected.available ? 'marcado como DISPONIBLE' : 'sin inventario'}{selected.active === false ? ' · lista cerrada' : ''}</Typography>
                  </div>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Button size="small" variant="outlined" startIcon={<Iconify icon="eva:person-add-outline" />} onClick={() => setAddOpen(true)} disabled={selected.active === false}>Anotar persona</Button>
                    <Button size="small" variant="contained" color="warning" startIcon={<Iconify icon="eva:bell-outline" />} onClick={() => { setNotifyMode(selectedIds.length ? 'selected' : 'next'); setNotifyOpen(true); }} disabled={queue.open.length === 0}>Avisar que llegó</Button>
                    <Button size="small" variant="text" startIcon={<Iconify icon="eva:edit-outline" />} onClick={() => setProdDialog({ ...selected })}>Editar</Button>
                  </Stack>
                </Stack>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox" />
                      <TableCell>#</TableCell>
                      <TableCell>Cliente</TableCell>
                      <TableCell>Contacto</TableCell>
                      <TableCell>Se anotó</TableCell>
                      <TableCell>Estado</TableCell>
                      <TableCell align="right">Acciones</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {queue.open.length === 0 && <TableRow><TableCell colSpan={7}><Typography variant="body2" color="text.secondary">Nadie en lista todavía.</Typography></TableCell></TableRow>}
                    {queue.open.map((e) => (
                      <TableRow key={e.id} hover selected={Boolean(checked[e.id])}>
                        <TableCell padding="checkbox"><Checkbox size="small" checked={Boolean(checked[e.id])} onChange={(ev) => setChecked({ ...checked, [e.id]: ev.target.checked })} /></TableCell>
                        <TableCell><b>{e.position}</b></TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>{e.name}</Typography>
                          {e.note && <Typography variant="caption" color="text.secondary">{e.note}</Typography>}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{e.phone ? formatPhoneDisplay(e.phone) : '—'}</Typography>
                          <Typography variant="caption" color="text.secondary">{e.email || 'sin email'}{(e.pushTokens || []).length ? ' · app' : ''}</Typography>
                        </TableCell>
                        <TableCell><Typography variant="caption">{fmt(e.joinedAt)}</Typography><br /><Typography variant="caption" color="text.secondary">{e.source === 'staff' ? `tienda (${e.createdBy || ''})` : e.source}</Typography></TableCell>
                        <TableCell>
                          <Chip size="small" color={STATUS[e.status].color} label={STATUS[e.status].label} />
                          {e.status === 'notified' && <Typography variant="caption" display="block" color="text.secondary">{fmt(e.notifiedAt)}{e.notifyCount > 1 ? ` · ${e.notifyCount} avisos` : ''}</Typography>}
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="Avisar solo a esta persona"><IconButton size="small" color="warning" onClick={() => { setChecked({ [e.id]: true }); setNotifyMode('selected'); setNotifyOpen(true); }}><Iconify icon="eva:bell-outline" /></IconButton></Tooltip>
                          <Tooltip title="Entregado / compró"><IconButton size="small" color="success" onClick={() => setStatus(e, 'fulfilled')}><Iconify icon="eva:checkmark-circle-2-outline" /></IconButton></Tooltip>
                          <Tooltip title="Quitar de la lista"><IconButton size="small" color="error" onClick={() => { if (window.confirm(`¿Quitar a ${e.name} de la lista?`)) setStatus(e, 'removed'); }}><Iconify icon="eva:close-circle-outline" /></IconButton></Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {queue.closed.length > 0 && (
                  <>
                    <Button size="small" sx={{ mt: 1 }} onClick={() => setShowClosed(!showClosed)}>{showClosed ? 'Ocultar' : 'Ver'} entregados / quitados ({queue.closed.length})</Button>
                    {showClosed && (
                      <Table size="small" sx={{ opacity: 0.7 }}>
                        <TableBody>
                          {queue.closed.map((e) => (
                            <TableRow key={e.id}>
                              <TableCell>{e.name}</TableCell>
                              <TableCell>{e.phone ? formatPhoneDisplay(e.phone) : e.email}</TableCell>
                              <TableCell><Chip size="small" color={STATUS[e.status] ? STATUS[e.status].color : 'default'} label={STATUS[e.status] ? STATUS[e.status].label : e.status} /></TableCell>
                              <TableCell><Typography variant="caption">{fmt(e.fulfilledAt || e.removedAt || e.joinedAt)}</Typography></TableCell>
                              <TableCell align="right"><Button size="small" onClick={() => setStatus(e, 'waiting')}>Volver a la lista</Button></TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    )}
                  </>
                )}
              </Card>
            ) : (!loading && <Alert severity="info">Escoge o crea un producto.</Alert>)}
          </Grid>
        </Grid>
      </Container>

      {/* Producto */}
      <Dialog open={Boolean(prodDialog)} onClose={() => { setProdDialog(null); setPhoto(null); }} maxWidth="sm" fullWidth>
        {prodDialog && (
          <>
            <DialogTitle>{prodDialog.id ? 'Editar producto' : 'Nuevo producto en lista de espera'}</DialogTitle>
            <DialogContent dividers>
              <Stack spacing={2}>
                <TextField autoFocus fullWidth label="Nombre del producto" value={prodDialog.name} onChange={(e) => setProdDialog({ ...prodDialog, name: e.target.value })} placeholder="Ej: Glock 19 Gen 5 · 9mm" />
                <TextField fullWidth multiline minRows={2} label="Descripción (la ve el cliente)" value={prodDialog.description || ''} onChange={(e) => setProdDialog({ ...prodDialog, description: e.target.value })} />
                <Stack direction="row" spacing={2} alignItems="center">
                  {(photo || prodDialog.imageUrl) && <Box component="img" src={photo ? URL.createObjectURL(photo) : prodDialog.imageUrl} alt="" sx={{ width: 96, height: 96, objectFit: 'cover', borderRadius: 1 }} />}
                  <div>
                    <Button variant="outlined" size="small" component="label" startIcon={<Iconify icon="eva:image-outline" />}>{photo ? photo.name : (prodDialog.imageUrl ? 'Cambiar foto' : 'Foto del producto')}<input hidden type="file" accept="image/*" onChange={(e) => setPhoto(e.target.files && e.target.files[0] ? e.target.files[0] : null)} /></Button>
                    {prodDialog.imageUrl && !photo && <Button size="small" color="error" onClick={() => setProdDialog({ ...prodDialog, imageUrl: '' })}>Quitar</Button>}
                    <Typography variant="caption" color="text.secondary" display="block">La ve el cliente como referencia en la app y en la web.</Typography>
                  </div>
                </Stack>
                <FormControlLabel control={<Switch checked={prodDialog.active !== false} onChange={(e) => setProdDialog({ ...prodDialog, active: e.target.checked })} />} label="Lista abierta (los clientes pueden anotarse)" />
                <FormControlLabel control={<Switch checked={Boolean(prodDialog.available)} onChange={(e) => setProdDialog({ ...prodDialog, available: e.target.checked })} />} label="Disponible ahora (el cliente lo ve como 'ya llegó')" />
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => { setProdDialog(null); setPhoto(null); }}>Cancelar</Button>
              <Button variant="contained" onClick={saveProduct} disabled={busy || !prodDialog.name.trim()}>{busy ? 'Guardando…' : 'Guardar'}</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Anotar persona */}
      <Dialog open={addOpen} onClose={() => setAddOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Anotar persona · {selected ? selected.name : ''}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12} sm={5}><TextField fullWidth label="Teléfono" value={add.phone} onChange={(e) => { setAdd({ ...add, phone: e.target.value }); setAddLookup('idle'); }} onBlur={lookupPhone} helperText={addLookup === 'found' ? 'Cuenta encontrada' : addLookup === 'notfound' ? 'Sin cuenta: se le crea (teléfono + PIN)' : 'Busca la cuenta por teléfono'} /></Grid>
            <Grid item xs={12} sm={7}><TextField fullWidth label="Nombre" value={add.name} onChange={(e) => setAdd({ ...add, name: e.target.value })} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth label="Email (para el aviso)" value={add.email} onChange={(e) => setAdd({ ...add, email: e.target.value })} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth label="Nota (opcional)" value={add.note} onChange={(e) => setAdd({ ...add, note: e.target.value })} placeholder="Ej: quiere 2, color negro" /></Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={submitAdd} disabled={busy || !add.name.trim() || phoneDigits(add.phone).length < 7}>Anotar</Button>
        </DialogActions>
      </Dialog>

      {/* Avisar */}
      <Dialog open={notifyOpen} onClose={() => setNotifyOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Avisar que llegó · {selected ? selected.name : ''}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Alert severity="info">Se envía email y notificación push (a los que tienen la app) y la entrada pasa a "Avisado". El producto queda marcado como disponible.</Alert>
            <TextField select fullWidth label="¿A quién?" value={notifyMode} onChange={(e) => setNotifyMode(e.target.value)}>
              <MenuItem value="next">A los próximos N en espera (por orden de llegada)</MenuItem>
              <MenuItem value="all">A todos los que están en espera ({queue.open.filter((e) => e.status === 'waiting').length})</MenuItem>
              <MenuItem value="selected" disabled={selectedIds.length === 0}>Solo a los marcados ({selectedIds.length})</MenuItem>
            </TextField>
            {notifyMode === 'next' && <TextField type="number" label="Cuántos" value={notifyCount} onChange={(e) => setNotifyCount(e.target.value)} inputProps={{ min: 1 }} helperText="Ej: llegaron 2 unidades → avisar a los 2 primeros" sx={{ maxWidth: 200 }} />}
            <TextField fullWidth multiline minRows={2} label="Mensaje adicional (opcional, va en el email)" value={notifyMsg} onChange={(e) => setNotifyMsg(e.target.value)} placeholder="Ej: Lo guardamos 48 horas. Precio $599 + IVU." />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNotifyOpen(false)} disabled={busy}>Cancelar</Button>
          <Button variant="contained" color="warning" onClick={submitNotify} disabled={busy}>{busy ? 'Enviando…' : 'Enviar aviso'}</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
